import { useMutation, useQuery } from "@apollo/client";
import {
  CreatePickupDocument,
  GenerateLaboratoryOrderDocumentDocument,
  GetPickupAvailabilityDocument,
  NotifyInternalPickupRequiredDocument,
} from "../../gql/generated/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FilteredPickupAvailability,
  filterPickupAvailabilitiesTime,
} from "../../utils/pickup.utils";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import {
  downloadBase64File,
  mergePDF,
  printBase64File,
} from "../../utils/files.utils";
import {
  CarrierType,
  ImprintType,
  TranslationsAvailable,
} from "../../types/enums";
import {
  Alert,
  Button,
  List,
  ListItem,
  Loader,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconNumber1,
  IconNumber2,
  IconNumber3,
  IconNumber4,
  IconNumber5,
} from "@tabler/icons-react";
import CreatePickup from "../modals/createPickup";

interface Props {
  orderId: string;
  pickupCarrier: CarrierType;
  orderReference: string;
  imprintType: ImprintType;
}

export const PrepareImprintPickup = ({
  orderId,
  pickupCarrier,
  orderReference,
  imprintType,
}: Props) => {
  const navigate = useNavigate();

  const [isUpdatePickupDateModalOpened, setIsUpdatePickupDateModalOpened] =
    useState(false);
  // Tracks the user-selected availability; falls back to the first available slot
  const [selectedAvailability, setSelectedAvailability] =
    useState<FilteredPickupAvailability | null>(null);
  const [pickupComment, setPickupComment] = useState<string>("");
  const [isLabelPrinted, setIsLabelPrinted] = useState(false);

  // Refs for values used in event handlers / timeouts that don't affect rendering
  const pickupLabelRef = useRef<string | null>(null);
  const laboratoryOrderDocumentRef = useRef<string | null>(null);
  const isFallbackTriggeredRef = useRef(false);
  const isLabelPrintedRef = useRef(false);
  const idleInteractionTimeoutRef = useRef<NodeJS.Timeout>(null);
  // Distinguishes a real unmount from the StrictMode fake unmount:
  // set to true via setTimeout so it's still false when StrictMode's
  // synchronous cleanup fires, but true when the component truly unmounts.
  const isMountCommittedRef = useRef(false);

  // Keep isLabelPrintedRef in sync for use in stable event-handler callbacks
  useEffect(() => {
    isLabelPrintedRef.current = isLabelPrinted;
  }, [isLabelPrinted]);

  const {
    data: pickupAvailabilityData,
    loading: pickupAvailabilityLoading,
    error: pickupAvailabilityError,
  } = useQuery(GetPickupAvailabilityDocument, {
    variables: {
      orderId,
    },
    fetchPolicy: "network-only",
  });
  const [createPickup, { loading: isPickupCreationLoading }] =
    useMutation(CreatePickupDocument);
  const [
    generateLaboratoryOrderDocument,
    { loading: isLaboratoryOrderDocumentLoading },
  ] = useMutation(GenerateLaboratoryOrderDocumentDocument);
  const [notifyInternalPickupRequired] = useMutation(
    NotifyInternalPickupRequiredDocument
  );

  const pickupAvailability = useMemo(
    () =>
      pickupAvailabilityData?.getPickupAvailability
        ? filterPickupAvailabilitiesTime(
            pickupAvailabilityData.getPickupAvailability
          )
        : undefined,
    [pickupAvailabilityData]
  );

  // Derive the active slot: user selection, or first available as default
  const activeAvailability = selectedAvailability ?? pickupAvailability?.[0];
  const pickupScheduledDate = activeAvailability?.pickupDate ?? null;
  const packageReadyTime =
    pickupCarrier === CarrierType.FEDEX_TNT
      ? (activeAvailability?.packageReadyTime ?? null)
      : null;
  const clinicClosingTime =
    pickupCarrier === CarrierType.FEDEX_TNT
      ? (activeAvailability?.clinicClosingTime ?? null)
      : null;

  // Safety net: if the practitioner leaves without downloading the label,
  // create the pickup on their behalf and notify support to forward the label.
  // useCallback with stable deps so this function can be used in event listeners.
  const handleCloseWithPickupFallback = useCallback(async () => {
    const shouldTriggerFallback =
      !pickupLabelRef.current && !isFallbackTriggeredRef.current;

    if (shouldTriggerFallback) {
      isFallbackTriggeredRef.current = true;
      // Fire-and-forget: never block navigation on the carrier/back response
      await notifyInternalPickupRequired({
        variables: {
          orderId,
          createPickup: true,
          dentistDidNotDownload: true,
        },
        context: {
          fetchOptions: {
            keepAlive: true,
          },
        },
      }).catch(error => {
        console.error("Error triggering pickup fallback:", error);
      });

      notifications.show({
        title: "Étiquette de retour non téléchargée",
        message:
          "Vous n'avez pas téléchargé votre étiquette de retour. Nous l'envoyons à notre équipe support qui vous la fera suivre par email pour que vous puissiez la coller sur le colis de retour.",
        color: "blue",
        autoClose: 7000,
      });
    }

    navigate(`/order/${orderId}`);
  }, [orderId, navigate, notifyInternalPickupRequired]);

  useEffect(() => {
    const timer = setTimeout(() => {
      isMountCommittedRef.current = true;
    }, 0);

    return () => {
      clearTimeout(timer);
      if (isMountCommittedRef.current) {
        handleCloseWithPickupFallback();
      }
      isMountCommittedRef.current = false;
    };
  }, [handleCloseWithPickupFallback]);

  const resetTimeout = useCallback(() => {
    if (idleInteractionTimeoutRef.current) {
      clearTimeout(idleInteractionTimeoutRef.current);
      idleInteractionTimeoutRef.current = null;
    }

    idleInteractionTimeoutRef.current = setTimeout(
      () => {
        handleCloseWithPickupFallback();
      },
      1000 * 60 * 5 // 5 minutes
    );
  }, [handleCloseWithPickupFallback]);

  // Automatically trigger fallback after 5 minutes of inactivity (safety net)
  useEffect(() => {
    resetTimeout();

    return () => {
      if (idleInteractionTimeoutRef.current) {
        clearTimeout(idleInteractionTimeoutRef.current);
        idleInteractionTimeoutRef.current = null;
      }
    };
  }, [resetTimeout]);

  // Automatically trigger fallback when the practitioner closes the tab without downloading (safety net)
  useEffect(() => {
    const handleBeforeUnload = (ev: BeforeUnloadEvent) => {
      ev.preventDefault();
      handleCloseWithPickupFallback();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleCloseWithPickupFallback]);

  if (
    (pickupAvailabilityLoading || !pickupAvailability) &&
    !pickupAvailabilityError
  ) {
    return (
      <Stack align="center">
        <Loader />
      </Stack>
    );
  }

  if (
    pickupAvailability?.length === 0 ||
    !pickupCarrier ||
    pickupAvailabilityError
  ) {
    return (
      <Stack align="center">
        <Alert title="Aucune disponibilité de ramassage trouvée" color="red">
          Nous n'avons trouvé aucune disponibilité de ramassage pour votre
          commande. Veuillez contacter le support pour résoudre ce problème.
        </Alert>
        <Button mt="md" onClick={handleCloseWithPickupFallback}>
          Voir ma commande
        </Button>
      </Stack>
    );
  }

  const itemLabel =
    imprintType === ImprintType.TRY_IN_RDV2
      ? "l'essayage de la prothèse partielle"
      : "la cire d'occlusion de la prothèse partielle";

  const firstAvailableDayStart = new Date(
    packageReadyTime
      ? `${pickupScheduledDate}T${packageReadyTime}`
      : pickupScheduledDate
  );

  const firstAvailableDayEnd = new Date(
    clinicClosingTime
      ? `${pickupScheduledDate}T${clinicClosingTime}`
      : pickupScheduledDate
  );

  const generatePickupLabel = async () => {
    try {
      const { data } = await createPickup({
        variables: {
          orderId,
          scheduledDate: pickupScheduledDate,
          packageReadyTime,
          clinicClosingTime,
          comment: pickupComment,
        },
      });

      pickupLabelRef.current = data?.createPickup;
      return data?.createPickup;
    } catch (error) {
      console.error("Error creating pickup:", error);
      throw new Error("Error creating pickup: " + error.message, {
        cause: error,
      });
    }
  };

  const generateLaboratoryOrderDocumentPdf = async () => {
    try {
      const { data } = await generateLaboratoryOrderDocument({
        variables: {
          orderId,
        },
      });

      laboratoryOrderDocumentRef.current =
        data?.generateLaboratoryOrderDocument;
      return data?.generateLaboratoryOrderDocument;
    } catch (error) {
      console.error("Error generating laboratory order document:", error);
      throw new Error(
        "Error generating laboratory order document: " + error.message,
        { cause: error }
      );
    }
  };

  const handlePrintOrDownload = async (action: "PRINT" | "DOWNLOAD") => {
    try {
      const documents = {
        pickupLabel: pickupLabelRef.current ?? (await generatePickupLabel()),
        laboratoryOrderDocument:
          laboratoryOrderDocumentRef.current ??
          (await generateLaboratoryOrderDocumentPdf().catch(() => {
            notifications.show({
              title: "Erreur lors de la génération du document de laboratoire",
              message:
                "Une erreur est survenue lors de la génération du document de laboratoire. Vous pouvez tout de même imprimer votre étiquette de transport.",
              color: "red",
            });

            return null;
          })),
      };

      if (!documents.laboratoryOrderDocument) {
        if (action === "PRINT") {
          printBase64File(documents.pickupLabel, ".pdf");
        } else {
          downloadBase64File(
            documents.pickupLabel,
            `etiquette-ramassage-${orderReference ?? "pickup"}`,
            ".pdf"
          );
        }
        return;
      }

      const mergedDocuments = await mergePDF(
        documents.pickupLabel,
        documents.laboratoryOrderDocument
      );
      if (action === "PRINT") {
        printBase64File(mergedDocuments, ".pdf");
      } else {
        downloadBase64File(
          mergedDocuments,
          `etiquette-ramassage-${orderReference ?? "pickup"}`,
          ".pdf"
        );
      }
    } catch (err) {
      notifications.show({
        title: "Erreur lors de la génération de l'étiquette de transport",
        message:
          "Veuillez réessayer ou contacter le support si le problème persiste",
        color: "red",
      });
    } finally {
      setIsLabelPrinted(true);
    }
  };

  const handlePrint = () => handlePrintOrDownload("PRINT");
  const handleDownload = () => handlePrintOrDownload("DOWNLOAD");

  const handlePickupDateChange = (
    _orderId: string,
    _orderReference: string,
    data: {
      date: string;
      comment: string | null;
    }
  ) => {
    if (!isLabelPrinted) {
      const newAvailability = pickupAvailability.find(
        availability => availability.pickupDate === data.date
      );

      if (!newAvailability) {
        return;
      }

      if (idleInteractionTimeoutRef.current) {
        resetTimeout();
      }

      setSelectedAvailability(newAvailability);
      setPickupComment(data.comment ?? "");
    }

    setIsUpdatePickupDateModalOpened(false);
  };

  return (
    <>
      <Modal
        opened={isUpdatePickupDateModalOpened}
        onClose={() => setIsUpdatePickupDateModalOpened(false)}
        size="auto"
      >
        <CreatePickup
          onEdit={() => {}}
          onSubmit={handlePickupDateChange}
          orderId={orderId}
          orderReference={orderReference}
          lang={TranslationsAvailable.FR}
          pickupCarrier={pickupCarrier}
          pickupAvailabilities={
            pickupAvailability?.map(avail => ({
              pickupDate: avail.pickupDate,
              readyTimeOptions: [avail.packageReadyTime],
              latestTimeOptions: [avail.clinicClosingTime],
            })) ?? []
          }
          disableTimeSelection
        />
      </Modal>
      <Stack mx="md" gap="xl">
        <Title order={3}>Préparez votre colis retour</Title>

        <Title order={4}>
          Date de ramassage prévue :{" "}
          {firstAvailableDayStart.toLocaleDateString("fr-FR", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          {pickupCarrier === CarrierType.FEDEX_TNT
            ? ` entre ${firstAvailableDayStart.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              })} et ${firstAvailableDayEnd.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : ""}
          <Tooltip
            label={
              isLabelPrinted
                ? "Vous ne pouvez plus modifier la date de ramassage après impression ou téléchargement de l'étiquette de transport."
                : "Modifier la date de ramassage"
            }
          >
            <Button
              variant="subtle"
              onClick={() => setIsUpdatePickupDateModalOpened(true)}
              disabled={isLabelPrinted}
            >
              Modifier
            </Button>
          </Tooltip>
        </Title>

        <Stack>
          <Text>
            Merci de cliquer sur le lien suivant pour récupérer l'étiquette de
            transport à coller sur votre colis :
          </Text>
          <Button
            loading={
              isPickupCreationLoading || isLaboratoryOrderDocumentLoading
            }
            onClick={handlePrint}
          >
            Imprimer l'étiquette
          </Button>
          <Button
            loading={
              isPickupCreationLoading || isLaboratoryOrderDocumentLoading
            }
            variant="light"
            onClick={handleDownload}
          >
            Télécharger l'étiquette
          </Button>

          <List spacing="sm">
            <ListItem
              icon={
                <ThemeIcon radius="xl">
                  <IconNumber1 size="16" />
                </ThemeIcon>
              }
            >
              Disposez {itemLabel} sur le modèle reçu afin d'assurer un
              transport optimal
            </ListItem>
            <ListItem
              icon={
                <ThemeIcon radius="xl">
                  <IconNumber2 size="16" />
                </ThemeIcon>
              }
            >
              Protègez vos travaux avec du papier-bulles.
            </ListItem>
            <ListItem
              icon={
                <ThemeIcon radius="xl">
                  <IconNumber3 size="16" />
                </ThemeIcon>
              }
            >
              Insérez la fiche de préparation dans le colis.
            </ListItem>
            <ListItem
              icon={
                <ThemeIcon radius="xl">
                  <IconNumber4 size="16" />
                </ThemeIcon>
              }
            >
              Collez l'étiquette retour sur le colis.
            </ListItem>
            <ListItem
              icon={
                <ThemeIcon radius="xl">
                  <IconNumber5 size="16" />
                </ThemeIcon>
              }
            >
              Un coursier viendra récupérer votre colis à la date prévue.
            </ListItem>
          </List>
        </Stack>

        <Stack align="flex-end">
          <Button size="md" onClick={handleCloseWithPickupFallback}>
            Voir ma commande
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
